<script setup lang="ts">
    import {computed, nextTick, onUnmounted, provide, ref, watch} from 'vue';
    import {storeToRefs} from 'pinia';
    import ChatActiveFilters from './products/ChatActiveFilters.vue';
    import ChatTranscript from './transcript/ChatTranscript.vue';
    import {chatStoreKey, createChatStore} from './core/chatStore';
    import type {TranscriptHandle, Vehicle} from './core/chatTypes';

    const props = defineProps<{
        conversationId: string;
        csrfToken: string;
        sendUrl: string;
        resetUrl: string;
        verifyReturnUrl: string;
        submitReturnUrl: string;
        verifyOrderStatusUrl: string;
        privacyUrl?: string;
        // services.claude.message_max_length - the same number SendChatMessageRequest validates.
        messageMaxLength: number;
        // Display seed only; the vehicle used for search is seeded server-side.
        initialVehicle?: Vehicle | null;
    }>();

    const emit = defineEmits<{(event: 'vehicle', vehicle: Vehicle | null): void}>();
    const draft = ref('');
    const draftInput = ref<HTMLInputElement | null>(null);
    const transcript = ref<TranscriptHandle | null>(null);
    const chat = createChatStore(props, props.initialVehicle);
    provide(chatStoreKey, chat);
    onUnmounted(() => chat.$dispose());
    const {entries, pending, typing, activeFilters, vehicle, lastTurnFirstEntryId} = storeToRefs(chat);
    watch(vehicle, (resolved) => emit('vehicle', resolved));
    watch(
        [typing, lastTurnFirstEntryId],
        async ([isTyping, entryId]) => {
            await nextTick();
            if (isTyping) transcript.value?.scrollToEnd();
            else if (entryId) {
                draftInput.value?.focus();
                transcript.value?.scrollToEntry(entryId);
            }
        },
        {flush: 'post'}
    );
    const started = computed(() => entries.value.length > 0);
    const sendable = computed(() => !pending.value && draft.value.trim() !== '');
    // Shown only near the limit, so maxlength never silently swallows the end of a message.
    const nearLimit = computed(() => draft.value.length >= props.messageMaxLength * 0.8);

    function submitDraft(): void {
        if (!sendable.value) return;
        const message = draft.value.trim();
        draft.value = '';
        chat.send(message);
    }

    async function reset(): Promise<void> {
        if (await chat.reset()) draft.value = '';
    }

    // For <chat-modal>: products ticked on the page around the chat rather than inside it.
    defineExpose({comparePageProducts: chat.comparePageProducts});
</script>

<template>
    <div class="chat">
        <ChatTranscript
            ref="transcript"
            class="chat__transcript" />

        <ChatActiveFilters
            v-if="activeFilters.length > 0"
            class="chat__filters" />

        <form
            class="composer"
            @submit.prevent="submitDraft">
            <!-- The input + paper-plane button of carpartsdirect's newsletter (partials/footer/newsletter3). -->
            <div class="newsletter-container composer__row">
                <input
                    ref="draftInput"
                    v-model="draft"
                    class="c-input composer__input"
                    type="text"
                    :maxlength="messageMaxLength"
                    :placeholder="started ? 'Je vraag of bericht…' : 'Stel je vraag over onderdelen, bestelling of retour…'"
                    :disabled="pending" />
                <button
                    type="submit"
                    class="click-search composer__send"
                    :disabled="!sendable"
                    :aria-label="pending ? 'Bezig…' : 'Verstuur'">
                    <i
                        class="fa fa-paper-plane"
                        aria-hidden="true"></i>
                </button>
            </div>

            <div class="composer__meta">
                <div class="composer__disclaimer text-muted">
                    <div
                        v-if="draft.length >= messageMaxLength"
                        class="mao-notification-auth mao-notification warn">
                        <i class="fa fa-exclamation-triangle"></i>
                        <span>Je tekst is te lang.</span>
                    </div>
                </div>
                <div class="composer__aside">
                    <button
                        type="button"
                        class="btn btn-link btn-xs"
                        :disabled="pending"
                        @click="reset">
                        Nieuwe chat
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .chat {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: $white;
        color: $black;
        font-family: 'Source Sans Pro', 'Segoe UI', system-ui, sans-serif;
    }

    .chat__transcript {
        background: #fdf8ea;
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
    }

    .chat__filters {
        padding: 10px var(--chat-pad) 0 calc(var(--chat-pad) + var(--chat-indent));
    }

    .composer {
        flex: 0 0 auto;
        padding: 14px 24px 16px;
    }

    .composer__row {
        display: flex;
        align-items: stretch;
    }

    .panel {
        border-radius: 0;
    }
    .composer__input {
        flex: 1 1 auto;
        min-width: 0;
        height: 40px;
        margin: 0;
        border: 1px solid $grey;
        border-right: 0;
        border-radius: 0 !important;
    }

    /* click-search is the shop's dark search button; the design's send button is the yellow one. */
    .composer__send {
        display: flex;
        flex: 0 0 46px;
        align-items: center;
        justify-content: center;
        width: 46px;
        height: 40px;
        border-radius: 0;
        background-color: $yellow;
        color: $black;
        font-size: 17px;
    }

    .composer__send:hover:not(:disabled) {
        background-color: $yellow-hover !important;
    }

    /* maoextra.css gives .click-search:hover a dark !important background, disabled or not. */
    .composer__send:disabled,
    .composer__send:disabled:hover {
        background-color: $yellow !important;
        opacity: 0.5;
        cursor: default;
    }

    .composer__meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 7px;
    }

    .composer__disclaimer {
        font-size: 12px;
        line-height: 1.4;
    }

    .composer__aside {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        gap: 12px;
        font-size: 12px;
    }

    @media (max-width: $chat-mobile) {
        .composer {
            padding: 12px 16px 14px;
        }

        .composer__meta {
            flex-direction: column;
            align-items: flex-start;
        }
    }
</style>

<style lang="scss" src="../../sass/chat.scss"></style>
